/**
 * Instancia WEB del CourseModuleRegistry (F2-A): registra los plugins v1
 * (lesson, resource) con sus componentes Solid LAZY y los metadatos de UI del
 * menú "añadir módulo" del authoring. F3 añade kinds (assignment/forum/video/
 * live_session) registrando aquí su plugin — sin tocar el authoring/player.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { Component } from "solid-js";
import {
  CourseModuleRegistry,
  createAssignmentModulePlugin,
  createForumModulePlugin,
  createLessonModulePlugin,
  createLiveSessionModulePlugin,
  createResourceModulePlugin,
  // MEDIA-F3F: module kind `video`
  createVideoModulePlugin,
} from "@space/shared";

/** Props comunes de los componentes de módulo (authoring y player). */
export interface ModuleComponentProps {
  courseId: string;
  moduleId: string;
  refId?: string | null;
}

export type ModuleComponent = Component<ModuleComponentProps>;

export const moduleRegistry = new CourseModuleRegistry<ModuleComponent>();

moduleRegistry.registerAll([
  createLessonModulePlugin<ModuleComponent>({
    // La lección se autora/reproduce con el flujo existente (bloques); estos
    // loaders existen para consumidores genéricos del registry.
    playerComponent: () =>
      import("./resource-module").then((m) => m.ModuleFallback as ModuleComponent),
  }),
  createResourceModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./resource-module").then((m) => m.ResourceModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./resource-module").then((m) => m.ResourceModuleViewer as ModuleComponent),
  }),
  // MEDIA-F3F: module kind `video` (player autónomo + autoría pick/upload).
  createVideoModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("@/features/media/components/video-module").then(
        (m) => m.VideoModuleEditor as ModuleComponent,
      ),
    playerComponent: () =>
      import("@/features/media/components/video-module").then(
        (m) => m.VideoModuleViewer as ModuleComponent,
      ),
  }),
  // F3-A: clase en vivo (thin). El player muestra la info de la sesión enlazada;
  // la pantalla de acceso a la videollamada la añade F3-G.
  createLiveSessionModulePlugin<ModuleComponent>({
    playerComponent: () =>
      import("./live-session-module").then((m) => m.LiveSessionModuleViewer as ModuleComponent),
  }),
  // F3-B: tarea evaluable (authoring con editor de rúbrica; player con entrega).
  createAssignmentModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./assignment-module").then((m) => m.AssignmentModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./assignment-module").then((m) => m.AssignmentModulePlayer as ModuleComponent),
  }),
  // F3-C: module kind `forum` (lista de hilos + vista de hilo + moderación).
  createForumModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./forum-module").then((m) => m.ForumModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./forum-module").then((m) => m.ForumModuleViewer as ModuleComponent),
  }),
]);

/** Metadatos de UI por kind (label del menú "añadir módulo"). */
export interface ModuleKindMeta {
  kind: string;
  label: MessageDescriptor;
  hint: MessageDescriptor;
  /** testid estable del botón de añadir (compat e2e: lesson = "add-lesson"). */
  testId: string;
}

export const MODULE_KIND_META: ModuleKindMeta[] = [
  {
    kind: "lesson",
    label: msg`Lesson`,
    hint: msg`Interactive lesson with learning blocks`,
    testId: "add-lesson",
  },
  {
    kind: "resource",
    label: msg`Resource`,
    hint: msg`File, link or text material`,
    testId: "add-module-resource",
  },
  // MEDIA-F3F: module kind `video`
  {
    kind: "video",
    label: msg`Video`,
    hint: msg`On-demand video with resume`,
    testId: "add-module-video",
  },
  {
    kind: "live_session",
    label: msg`Live class`,
    hint: msg`Live session linked to a scheduled class`,
    testId: "add-module-live_session",
  },
  {
    kind: "assignment",
    label: msg`Assignment`,
    hint: msg`Gradable task with optional rubric and submission`,
    testId: "add-module-assignment",
  },
  // F3-C: module kind `forum`
  {
    kind: "forum",
    label: msg`Forum`,
    hint: msg`Cohort discussion: threads and replies`,
    testId: "add-module-forum",
  },
];

export function moduleKindMeta(kind: string): ModuleKindMeta | undefined {
  return MODULE_KIND_META.find((m) => m.kind === kind);
}
